import React, { Suspense } from 'react';
import { lazyWithRetry } from './utils/lazyWithRetry';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { PRODUCTS_PATH } from './constants/advertisingNav';
import AppToaster from './components/ui/AppToaster';
import Layout from './components/Layout';
import TokenRefreshManager from './components/TokenRefreshManager';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { AmazonProfileProvider } from './contexts/AmazonProfileContext';
import { MetricsDateRangeProvider } from './contexts/MetricsDateRangeContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { readDashboardLayoutPreference, DASHBOARD_LAYOUT_CLASSIC } from './constants/dashboardLayout';

// Eager load critical auth pages
import Login from './pages/Login';
import LoginQc from './pages/LoginQc';
import StoreSelection from './pages/StoreSelection';
import StoreHubShell from './components/layout/StoreHubShell';
import OnboardingSuccess from './pages/OnboardingSuccess';
import OnboardingStoreSelection from './pages/OnboardingStoreSelection';
import AmazonCallback from './pages/AmazonCallback';
import SellerSignup from './pages/SellerSignup';
import ResetPassword from './pages/ResetPassword';
import PageSuspenseFallback from './components/ui/PageSuspenseFallback';

// Lazy load all other pages for faster initial load
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));
const DashboardBeta = lazyWithRetry(() => import('./pages/DashboardBeta'));
const Products = lazyWithRetry(() => import('./pages/Products'));
const Campaigns = lazyWithRetry(() => import('./pages/Campaigns'));
const CampaignHub = lazyWithRetry(() => import('./pages/CampaignHub'));
const Portfolios = lazyWithRetry(() => import('./pages/advertising/Portfolios'));
const AdvertisingBudgets = lazyWithRetry(() => import('./pages/advertising/Budgets'));
const AdvertisingAmazonRules = lazyWithRetry(() => import('./pages/advertising/AmazonRules'));
const AdvertisingDrafts = lazyWithRetry(() => import('./pages/advertising/Drafts'));
const TargetingLibraryPage = lazyWithRetry(() => import('./pages/advertising/TargetingLibrary'));
const AdvertisingHistory = lazyWithRetry(() => import('./pages/advertising/History'));
const AdvertisingSettings = lazyWithRetry(() => import('./pages/advertising/AdvertisingSettings'));
const CampaignDetails = lazyWithRetry(() => import('./pages/CampaignDetails'));
const AdGroupDetails = lazyWithRetry(() => import('./pages/AdGroupDetails'));
const CampaignLauncher = lazyWithRetry(() => import('./pages/CampaignLauncher'));
const UserManagement = lazyWithRetry(() => import('./pages/UserManagement'));
const AdminAccessHub = lazyWithRetry(() => import('./pages/AdminAccessHub'));
const Keywords = lazyWithRetry(() => import('./pages/Keywords'));
const MasterBusinessReport = lazyWithRetry(() => import('./pages/MasterBusinessReport'));
const Settings = lazyWithRetry(() => import('./pages/Settings'));
const AccountSettings = lazyWithRetry(() => import('./pages/AccountSettings'));
const BulkOperations = lazyWithRetry(() => import('./pages/BulkOperations'));
const AdminUnifiedReporting = lazyWithRetry(() => import('./pages/AdminUnifiedReporting'));
const AdminMarketingStream = lazyWithRetry(() => import('./pages/AdminMarketingStream'));
const AdminApiHealth = lazyWithRetry(() => import('./pages/AdminApiHealth'));
const QCommercePortalAccountsHubPage = lazyWithRetry(() => import('./pages/QCommercePortalAccountsHubPage'));
const Automation = lazyWithRetry(() => import('./pages/Automation'));
const CategorizationHub = lazyWithRetry(() => import('./pages/CategorizationHub'));
const CategoryPerformance = lazyWithRetry(() => import('./pages/CategoryPerformance'));
const AgencySuiteLayout = lazyWithRetry(() => import('./components/agency/AgencySuiteLayout'));
const AgencyCategorizationPage = lazyWithRetry(() => import('./pages/agency/AgencyCategorizationPage'));
const AgencyTextCategorizationPage = lazyWithRetry(() => import('./pages/agency/AgencyTextCategorizationPage'));
const AgencyPlaceholderPage = lazyWithRetry(() => import('./pages/agency/AgencyPlaceholderPage'));
const AgencyAsinCategorizationPage = lazyWithRetry(() => import('./pages/agency/AgencyAsinCategorizationPage'));
const AgenticAI = lazyWithRetry(() => import('./pages/AgenticAI'));
const QCommerceBrandApp = lazyWithRetry(() => import('./qcommerce/QCommerceBrandApp'));

function BlinkitLegacyRedirect() {
  const { brandId } = useParams();
  return <Navigate to={`/qc/blinkit/brands/${brandId}/ads/summary`} replace />;
}

function defaultAppPath(user) {
  if (!user) return '/login';
  if (user.role === 'store_owner') return '/dashboard';
  if (['account_manager', 'moderator', 'admin', 'super_admin'].includes(user.role)) {
    return '/dashboard';
  }
  return '/stores';
}

/** Legacy campaign-hub products URL → top-level Products (preserves query string). */
function CampaignHubProductsRedirect() {
  const location = useLocation();
  return <Navigate to={`${PRODUCTS_PATH}${location.search}`} replace />;
}

function HomeRedirect() {
  const { user, loading } = useAuth();
  if (loading) {
    return <PageSuspenseFallback label="Loading…" />;
  }
  return <Navigate to={defaultAppPath(user)} replace />;
}

/** `/dashboard` → beta or classic per saved preference (default beta). */
function DashboardEntry() {
  const pref = readDashboardLayoutPreference();
  const target = pref === DASHBOARD_LAYOUT_CLASSIC ? '/dashboard/classic' : '/dashboard/beta';
  return <Navigate to={target} replace />;
}

// Protected Route component
function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageSuspenseFallback label="Loading…" />;
  }
  
  return user ? children : <Navigate to="/login" />;
}

// Public Route component (redirect to stores if already logged in)
function PlatformAdminRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageSuspenseFallback label="Loading…" />;
  }

  if (!user) return <Navigate to="/login" />;
  if (!['admin', 'super_admin'].includes(user.role)) return <Navigate to="/stores" replace />;
  return children;
}

function UserManagementRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <PageSuspenseFallback label="Loading…" />;
  }

  if (!user) return <Navigate to="/login" />;
  if (!['admin', 'super_admin', 'moderator'].includes(user.role)) {
    return <Navigate to="/stores" replace />;
  }
  return children;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <PageSuspenseFallback label="Loading…" />;
  }
  
  if (user) {
    return <Navigate to={defaultAppPath(user)} />;
  }
  
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AmazonProfileProvider>
          <MetricsDateRangeProvider>
          <Router>
          <div className="App">
            <AppToaster />
            
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              } />
              <Route path="/login/qc" element={
                <PublicRoute>
                  <LoginQc />
                </PublicRoute>
              } />
              <Route path="/seller/signup" element={
                <PublicRoute>
                  <SellerSignup />
                </PublicRoute>
              } />
              <Route path="/reset-password" element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              } />
              
              {/* Onboarding Routes (Public) */}
              <Route path="/onboarding/success" element={<OnboardingSuccess />} />
              <Route path="/onboarding/select-stores" element={<OnboardingStoreSelection />} />
              <Route path="/auth/amazon-callback" element={<AmazonCallback />} />
              
              {/* Store hub + administration (no workspace sidebar) */}
              <Route element={
                <ProtectedRoute>
                  <StoreHubShell />
                </ProtectedRoute>
              }>
                <Route path="stores" element={<StoreSelection />} />
                <Route path="user-management" element={
                  <UserManagementRoute>
                    <Suspense fallback={<PageSuspenseFallback />}>
                      <UserManagement />
                    </Suspense>
                  </UserManagementRoute>
                } />
                <Route path="admin/qc-portal-accounts" element={
                  <UserManagementRoute>
                    <Suspense fallback={<PageSuspenseFallback />}>
                      <QCommercePortalAccountsHubPage />
                    </Suspense>
                  </UserManagementRoute>
                } />
                <Route path="account" element={
                  <Suspense fallback={<PageSuspenseFallback />}>
                    <AccountSettings />
                  </Suspense>
                } />
                <Route path="settings" element={
                  <PlatformAdminRoute>
                    <Suspense fallback={<PageSuspenseFallback />}>
                      <Settings />
                    </Suspense>
                  </PlatformAdminRoute>
                } />
                <Route path="admin/access-hub" element={
                  <PlatformAdminRoute>
                    <Suspense fallback={<PageSuspenseFallback />}>
                      <AdminAccessHub />
                    </Suspense>
                  </PlatformAdminRoute>
                } />
                <Route path="admin/report-generation" element={<Navigate to="/admin/unified-reporting" replace />} />
                <Route path="admin/unified-reporting" element={
                  <PlatformAdminRoute>
                    <Suspense fallback={<PageSuspenseFallback />}>
                      <AdminUnifiedReporting />
                    </Suspense>
                  </PlatformAdminRoute>
                } />
                <Route path="admin/marketing-stream" element={
                  <PlatformAdminRoute>
                    <Suspense fallback={<PageSuspenseFallback />}>
                      <AdminMarketingStream />
                    </Suspense>
                  </PlatformAdminRoute>
                } />
                <Route path="admin/api-health" element={
                  <PlatformAdminRoute>
                    <Suspense fallback={<PageSuspenseFallback />}>
                      <AdminApiHealth />
                    </Suspense>
                  </PlatformAdminRoute>
                } />
                <Route path="token-refresh" element={
                  <PlatformAdminRoute>
                    <TokenRefreshManager />
                  </PlatformAdminRoute>
                } />
              </Route>

              {/* Protected Routes - workspace (Dashboard, Campaigns, …) */}
              <Route path="/qc/:platform/brands/:brandId/*" element={
                <ProtectedRoute>
                  <Suspense fallback={<PageSuspenseFallback />}>
                    <QCommerceBrandApp />
                  </Suspense>
                </ProtectedRoute>
              } />
              <Route path="/blinkit/brands/:brandId/*" element={
                <ProtectedRoute>
                  <BlinkitLegacyRedirect />
                </ProtectedRoute>
              } />
              
              {/* Workspace — Dashboard, Campaigns, etc. */}
              <Route path="/" element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }>
                <Route index element={<HomeRedirect />} />
                <Route path="dashboard" element={<DashboardEntry />} />
                <Route path="dashboard/classic" element={
                  <Suspense fallback={<PageSuspenseFallback />}>
                    <Dashboard />
                  </Suspense>
                } />
                <Route path="dashboard/beta" element={
                  <Suspense fallback={<PageSuspenseFallback />}>
                    <DashboardBeta />
                  </Suspense>
                } />
                <Route path="products" element={
                  <Suspense fallback={<PageSuspenseFallback />}>
                    <Products />
                  </Suspense>
                } />
                <Route path="campaigns" element={
                  <Suspense fallback={<PageSuspenseFallback />}>
                    <CampaignHub />
                  </Suspense>
                }>
                  <Route index element={<Navigate to="list" replace />} />
                  <Route path="portfolios" element={
                    <Suspense fallback={<PageSuspenseFallback embedded />}>
                      <Portfolios />
                    </Suspense>
                  } />
                  <Route path="list" element={
                    <Suspense fallback={<PageSuspenseFallback embedded />}>
                      <Campaigns />
                    </Suspense>
                  } />
                  <Route path="drafts" element={
                    <Suspense fallback={<PageSuspenseFallback embedded />}>
                      <AdvertisingDrafts />
                    </Suspense>
                  } />
                  <Route path="targeting-library" element={
                    <Suspense fallback={<PageSuspenseFallback embedded />}>
                      <TargetingLibraryPage />
                    </Suspense>
                  } />
                  <Route path="products" element={<CampaignHubProductsRedirect />} />
                  <Route path="keywords" element={
                    <Suspense fallback={<PageSuspenseFallback embedded />}>
                      <Keywords />
                    </Suspense>
                  } />
                  <Route path="targeting" element={<Navigate to="/campaigns/keywords" replace />} />
                  <Route path="budgets" element={
                    <Suspense fallback={<PageSuspenseFallback embedded />}>
                      <AdvertisingBudgets />
                    </Suspense>
                  } />
                  <Route path="history" element={
                    <Suspense fallback={<PageSuspenseFallback embedded />}>
                      <AdvertisingHistory />
                    </Suspense>
                  } />
                  <Route path="bulk" element={
                    <Suspense fallback={<PageSuspenseFallback embedded />}>
                      <BulkOperations />
                    </Suspense>
                  } />
                  <Route path="rules" element={
                    <Suspense fallback={<PageSuspenseFallback embedded />}>
                      <AdvertisingAmazonRules />
                    </Suspense>
                  } />
                  <Route path="settings" element={
                    <Suspense fallback={<PageSuspenseFallback embedded />}>
                      <AdvertisingSettings />
                    </Suspense>
                  } />
                  <Route path=":campaignId/ad-groups/:adGroupId" element={
                    <Suspense fallback={<PageSuspenseFallback />}>
                      <AdGroupDetails />
                    </Suspense>
                  } />
                  <Route path=":campaignId" element={
                    <Suspense fallback={<PageSuspenseFallback />}>
                      <CampaignDetails />
                    </Suspense>
                  } />
                </Route>
                <Route path="campaign-launcher" element={
                  <Suspense fallback={<PageSuspenseFallback />}>
                    <CampaignLauncher />
                  </Suspense>
                } />
                <Route path="campaign-launcher/bulk" element={<Navigate to="/campaign-launcher" replace />} />
                <Route path="keywords" element={<Navigate to="/campaigns/keywords" replace />} />
                <Route path="reports" element={
                  <Suspense fallback={<PageSuspenseFallback />}>
                    <MasterBusinessReport />
                  </Suspense>
                } />
                <Route path="reports/master" element={<Navigate to="/reports" replace />} />
                <Route path="category-performance" element={
                  <Suspense fallback={<PageSuspenseFallback />}>
                    <CategoryPerformance />
                  </Suspense>
                } />
                <Route path="bulk-operations" element={<Navigate to="/campaigns/bulk" replace />} />
                <Route path="automation/*" element={
                  <Suspense fallback={<PageSuspenseFallback />}>
                    <Automation />
                  </Suspense>
                } />
                <Route path="categorization" element={
                  <Suspense fallback={<PageSuspenseFallback />}>
                    <CategorizationHub />
                  </Suspense>
                } />
                <Route path="agency" element={
                  <Suspense fallback={<PageSuspenseFallback />}>
                    <AgencySuiteLayout />
                  </Suspense>
                }>
                  <Route index element={<Navigate to="text-categorization" replace />} />
                  <Route path="categorization" element={<AgencyCategorizationPage />} />
                  <Route path="text-categorization" element={<AgencyTextCategorizationPage />} />
                  <Route path="asin" element={<AgencyAsinCategorizationPage />} />
                  <Route path="blacklist" element={<Navigate to="/automation/st_negation" replace />} />
                  <Route path="hourly-live" element={<Navigate to="/automation/scheduler" replace />} />
                </Route>
                <Route path="agentic-ai" element={
                  <Suspense fallback={<PageSuspenseFallback />}>
                    <AgenticAI />
                  </Suspense>
                } />
              </Route>
              
              {/* Catch all route */}
              <Route path="*" element={<Navigate to="/stores" replace />} />
            </Routes>
          </div>
        </Router>
          </MetricsDateRangeProvider>
      </AmazonProfileProvider>
    </AuthProvider>
    </ThemeProvider>
  );
}

export default App;







